import { Component } from "react";
import PropTypes from "prop-types";
import { Card, Table, Badge, Spinner, Alert, Button } from "react-bootstrap";
import { FaFilePdf, FaExternalLinkAlt } from "react-icons/fa";
import { withTranslation } from "react-i18next";
import api from "../../services/api";
import { formataDataHoraExibicao } from "../../common/utils";

/**
 * Indicações ligadas a este cadastro, nos dois sentidos:
 *
 *  - `papel="medico"`   → profissionais que ELE indicou (cadastro do médico);
 *  - `papel="indicado"` → médicos que indicaram ELE (cadastro do profissional).
 *
 * Inclui as encerradas: o escopo pede que o histórico fique preservado.
 *
 * A aba só é montada em programa que oferece indicação; quem decide isso é a tela de cadastro.
 */
class PrestadorIndicacoes extends Component {
    constructor(props) {
        super(props);
        this.state = { indicacoes: [], carregando: true, erro: "" };
    }

    componentDidMount() {
        this.carregar();
    }

    componentDidUpdate(anterior) {
        if (anterior.prestadorId !== this.props.prestadorId ||
            anterior.programaId !== this.props.programaId) {
            this.carregar();
        }
    }

    carregar = async () => {
        const { prestadorId, programaId } = this.props;
        if (!prestadorId) {
            this.setState({ carregando: false, indicacoes: [] });
            return;
        }

        this.setState({ carregando: true, erro: "" });
        try {
            const campo = this.props.papel === "indicado" ? "indicado_id" : "medico_id";
            const params = [`${campo}=${prestadorId}`, "limit=100"];
            if (programaId) params.push(`programa_id=${programaId}`);
            const { data } = await api.get(`/pessoas/indicacoes-profissionais/?${params.join("&")}`);
            this.setState({ carregando: false, indicacoes: data.results || [] });
        } catch (error) {
            const detalhe = error.response?.data?.detail;
            this.setState({
                carregando: false,
                erro: detalhe || this.props.t("indicacoes:cadastro.loadError"),
            });
        }
    };

    renderSituacao = (i) => {
        const { t } = this.props;
        if (i.status === "cancelada") return <Badge bg="secondary">{t("indicacoes:profissionais.status.cancelada")}</Badge>;
        if (i.status === "rascunho") return <Badge bg="warning">{t("indicacoes:profissionais.status.rascunho")}</Badge>;
        if (!i.dados_indicado) return <Badge bg="info">{t("indicacoes:profissionais.term.awaitingRegistration")}</Badge>;
        return <Badge bg="success">{t("indicacoes:profissionais.status.ativa")}</Badge>;
    };

    render() {
        const { t } = this.props;
        const { indicacoes, carregando, erro } = this.state;
        const doIndicado = this.props.papel === "indicado";

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <h6 className="text-muted">{doIndicado
                        ? t("indicacoes:cadastro.titleReferred")
                        : t("indicacoes:cadastro.titleDoctor")}</h6>
                    <p className="text-muted small">
                        {t("indicacoes:cadastro.historyHint")}
                    </p>

                    {carregando ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : erro ? (
                        <Alert variant="danger" className="mb-0">{erro}</Alert>
                    ) : indicacoes.length === 0 ? (
                        <p className="text-muted mb-0">{doIndicado
                            ? t("indicacoes:cadastro.emptyReferred")
                            : t("indicacoes:cadastro.emptyDoctor")}</p>
                    ) : (
                        <Table responsive className="custom-table">
                            <thead>
                                <tr>
                                    <th>{doIndicado ? t("indicacoes:cadastro.doctorWhoReferred") : t("indicacoes:assinatura.table.referred")}</th>
                                    <th>{doIndicado ? t("indicacoes:cadastro.crm") : t("indicacoes:profissionais.table.council")}</th>
                                    <th>{t("indicacoes:cadastro.registration")}</th>
                                    <th>{t("indicacoes:profissionais.table.referredOn")}</th>
                                    <th>{t("indicacoes:profissionais.table.status")}</th>
                                    <th>{t("indicacoes:profissionais.table.term")}</th>
                                    <th className="text-end">{t("indicacoes:profissionais.table.declaration")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {indicacoes.map((i) => (
                                    <tr key={i.id}>
                                        <td>{doIndicado ? (i.dados_medico?.nome || "-") : i.nome_indicado}</td>
                                        <td>
                                            {doIndicado
                                                ? [i.dados_medico?.registro, i.dados_medico?.registro_uf]
                                                    .filter(Boolean).join("/") || "-"
                                                : [i.conselho, i.registro, i.registro_uf].filter(Boolean).join(" ") || "-"}
                                        </td>
                                        <td>
                                            {/* leva direto ao cadastro: sem isto é preciso ir procurar em Provedores */}
                                            {doIndicado ? (
                                                i.dados_medico ? (
                                                    <a href={`/medicos/edit/${i.dados_medico.id}`}
                                                       target="_blank" rel="noopener noreferrer">
                                                        {i.dados_medico.nome} <FaExternalLinkAlt size={10} />
                                                    </a>
                                                ) : <span className="text-muted small">—</span>
                                            ) : i.dados_indicado ? (
                                                <a href={`/prestadores/edit/${i.dados_indicado.id}`}
                                                   target="_blank" rel="noopener noreferrer">
                                                    {i.dados_indicado.nome} <FaExternalLinkAlt size={10} />
                                                </a>
                                            ) : <span className="text-muted small">{t("indicacoes:cadastro.notLinked")}</span>}
                                        </td>
                                        <td>{i.data_indicacao ? formataDataHoraExibicao(i.data_indicacao) : "-"}</td>
                                        <td>{this.renderSituacao(i)}</td>
                                        <td>
                                            {!i.data_aceite_termo ? (
                                                <span className="text-muted small">—</span>
                                            ) : i.termo_comprovante_url ? (
                                                <a href={i.termo_comprovante_url} target="_blank"
                                                   rel="noopener noreferrer" title={t("indicacoes:indicados.openSignedTerm")}>
                                                    <Badge bg="success">{t("indicacoes:indicados.accepted")}</Badge>
                                                </a>
                                            ) : <Badge bg="success">{t("indicacoes:indicados.accepted")}</Badge>}
                                        </td>
                                        <td className="text-end">
                                            {i.declaracao_url ? (
                                                <Button variant="light" size="sm" href={i.declaracao_url}
                                                        target="_blank" rel="noopener noreferrer">
                                                    <FaFilePdf className="me-1" /> {t("indicacoes:profissionais.view")}
                                                </Button>
                                            ) : <span className="text-muted small">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        );
    }
}

PrestadorIndicacoes.propTypes = {
    t: PropTypes.func.isRequired,
    prestadorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    programaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    //"medico" (padrão): quem ele indicou. "indicado": quem indicou ele.
    papel: PropTypes.oneOf(["medico", "indicado"]),
};

export default withTranslation(["indicacoes", "common"])(PrestadorIndicacoes);
